import type { TweetReply, ContentQualityMetrics } from '@podx/core/types';
import { config } from '@podx/core/config';
import { logger } from '@podx/core/logger';

export class GenericContentDetector {
  // Common generic reply templates
  private genericTemplates = [
    // Engagement farming
    /^(nice|great|awesome|good|cool)\s*(post|tweet|share|one)?!*$/i,
    /^(thanks?|thx)\s*(for\s*)?(sharing|this|the\s*(info|update))?!*$/i,
    /^(this|that)\s*(is\s*)?(amazing|incredible|huge|big|great)!*$/i,
    /^(love|like)\s+(this|it|that)!*$/i,
    /^(so\s+)?(true|real|accurate)!*$/i,
    
    // Generic crypto responses
    /^(to\s+the\s+)?moon!*\s*🚀*$/i,
    /^(wen|when)\s+(moon|lambo|pump)\?*$/i,
    /^(hodl|diamond\s+hands)\s*💎*\s*🙌*$/i,
    /^(bullish|bearish)\s*on\s*\$?\w+\s*!*$/i,
    /^(bullish|bearish)\s*!*$/i,
    /^(lfg|let\'s\s+go)!+$/i,
    /^(gm|good\s+morning)\s*(fam|family|everyone|all)?!*$/i,
    /^(wagmi|we\'re\s+all\s+gonna\s+make\s+it)!*$/i,
    
    // Generic questions
    /^(what|which)\s+(coin|token|crypto)\s+(is\s+)?(this|next)\?*$/i,
    /^(how\s+)?(much|many)\s+\$?\w+\s+(should|do)\s+i\s+(buy|get)\?*$/i,
    /^(where|how)\s+(can|do)\s+i\s+buy\s+(this|\$?\w+)\?*$/i,
    /^(is\s+this\s+)?(financial|investment)\s+advice\?*$/i,
    
    // Low effort responses
    /^(yes|no|maybe|true|false|right|correct)\s*!*$/i,
    /^(first|second|third|1st|2nd|3rd)\s*!*$/i,
    /^\d+$/, // Just numbers
    /^(ok|okay|k)\s*!*$/i,
    /^(lol|lmao|lmfao|haha|hehe)\s*!*$/i,
    
    // Generic promotional
    /^(check\s+out|follow)\s+@\w+$/i,
    /^(dm|pm)\s+me\s+(for|about)/i,
    /^(join|visit)\s+(our|my)\s+(telegram|discord|website)/i,
    /^(free\s+)?(airdrop|giveaway)\s*(alert|live)?\s*!*$/i,
    
    // Copy-paste indicators
    /^(copied|reposted|shared)\s*(from|by)/i,
    /^(not\s+)?(financial|investment)\s+advice$/i,
    /^(dyor|do\s+your\s+own\s+research)$/i,
  ];

  // Emotional manipulation patterns
  private manipulationPatterns = [
    /don\'?t\s+miss\s+(out\s+)?on\s+this/i,
    /last\s+chance\s+to/i,
    /act\s+(fast|now|quickly)/i,
    /limited\s+(time|offer)/i,
    /only\s+\d+\s+(left|remaining)/i,
    /(urgent|breaking|alert)[:!]/i,
    /pump\s+(incoming|soon|alert)/i,
    /next\s+\d+x\s+(gem|coin)/i,
    /guaranteed\s+(profit|gains|returns)/i,
    /insider\s+(info|tip|knowledge)/i,
  ];

  // Bot-like patterns
  private botPatterns = [
    /^@\w+\s+/,  // Starts with mention (often bot replies)
    /\b(bot|automated|script)\b/i,
    /repeated\s+\d+\s+times/i,
    /generated\s+(automatically|by)/i,
  ];

  // Quality indicators (positive signals)
  private qualityIndicators = [
    // Technical analysis
    /\b(support|resistance|fibonacci|rsi|macd|bollinger|candlestick)\b/i,
    // Specific price/percentage mentions
    /\$[\d,]+\.?\d*|\d+\.\d+%/,
    // Time-specific references
    /\b(yesterday|today|tomorrow|this\s+week|next\s+month)\b/i,
    // Data/chart references
    /\b(chart|graph|data|analysis|report|study)\b/i,
    // Reasoning words
    /\b(because|since|due\s+to|given\s+that|considering)\b/i,
  ];

  analyzeContentQuality(reply: TweetReply): ContentQualityMetrics {
    const text = reply.text.toLowerCase().trim();
    
    logger.debug('Analyzing content quality', {
      operation: 'content_quality_analysis',
      username: reply.username,
      textLength: text.length
    });

    // Check against generic templates
    let genericityScore = 0;
    let templateMatch = '';
    
    for (const template of this.genericTemplates) {
      if (template.test(text)) {
        genericityScore = 0.9;
        templateMatch = template.source;
        logger.debug('Template match found', {
          operation: 'template_match',
          username: reply.username,
          template: template.source
        });
        break;
      }
    }

    // If no exact template match, check for partial generic patterns
    if (genericityScore === 0) {
      genericityScore = this.calculatePartialGenericity(text);
    }
    
    // Check for manipulation patterns
    let manipulationScore = 0;
    for (const pattern of this.manipulationPatterns) {
      if (pattern.test(text)) {
        manipulationScore += 0.3;
      }
    }
    manipulationScore = Math.min(1, manipulationScore);
    
    // Check for bot patterns
    let botScore = 0;
    for (const pattern of this.botPatterns) {
      if (pattern.test(text)) {
        botScore += 0.4;
      }
    }
    botScore = Math.min(1, botScore);
    
    // Calculate originality and information density
    const originalityScore = this.calculateOriginality(text);
    const informationDensity = this.calculateInformationDensity(text, reply);
    const credibilityMarkers = this.extractCredibilityMarkers(text);
    const speculationLevel = this.calculateSpeculationLevel(text);
    const educationalValue = this.calculateEducationalValue(text);
    
    // Calculate overall spam probability
    const spamProbability = Math.min(1, 
      genericityScore * 0.35 + 
      manipulationScore * 0.25 + 
      botScore * 0.25 +
      (1 - originalityScore) * 0.15
    );
    
    const metrics: ContentQualityMetrics = {
      genericityScore: Math.min(1, genericityScore),
      templateMatch: templateMatch || undefined,
      spamProbability,
      manipulationScore,
      originalityScore,
      informationDensity,
      credibilityMarkers,
      speculationLevel,
      educationalValue
    };

    logger.debug('Content quality analysis complete', {
      operation: 'content_quality_complete',
      username: reply.username,
      metrics: {
        genericityScore: metrics.genericityScore,
        spamProbability: metrics.spamProbability,
        originalityScore: metrics.originalityScore
      }
    });

    return metrics;
  }

  private calculatePartialGenericity(text: string): number {
    const words = text.split(/\s+/);
    let genericWords = 0;
    
    const commonGenericWords = [
      'nice', 'great', 'good', 'cool', 'awesome', 'amazing',
      'true', 'yes', 'no', 'right', 'correct', 'exactly',
      'this', 'that', 'thanks', 'thx', 'lol', 'haha'
    ];
    
    for (const word of words) {
      if (commonGenericWords.includes(word.toLowerCase())) {
        genericWords++;
      }
    }
    
    const genericRatio = genericWords / Math.max(words.length, 1);
    
    // Short messages with high generic word ratio are likely generic
    if (words.length <= 3 && genericRatio > 0.5) {
      return 0.8;
    } else if (words.length <= 5 && genericRatio > 0.6) {
      return 0.7;
    } else if (genericRatio > 0.4) {
      return 0.5;
    }
    
    return 0;
  }

  private calculateOriginality(text: string): number {
    // Factors: length, unique words ratio, sentence structure variety
    const words = text.split(/\s+/);
    const uniqueWords = new Set(words.filter(w => w.length > 2)).size; // Exclude very short words
    const uniqueRatio = uniqueWords / Math.max(words.length, 1);
    
    const lengthScore = Math.min(1, text.length / 100); // Longer = potentially more original
    const complexityScore = this.calculateComplexity(text);
    
    // Check for quality indicators
    let qualityBonus = 0;
    for (const indicator of this.qualityIndicators) {
      if (indicator.test(text)) {
        qualityBonus += 0.1;
      }
    }
    qualityBonus = Math.min(0.3, qualityBonus);
    
    return Math.min(1, (uniqueRatio + lengthScore + complexityScore + qualityBonus) / 3);
  }

  private calculateComplexity(text: string): number {
    // Sentence variety, punctuation use, specific vs generic language
    const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0);
    const hasNumbers = /\d/.test(text);
    const hasSpecificTerms = /\$\w+|0x[a-f0-9]+|\w+\.io|\w+\.com|@\w+/i.test(text);
    const hasPunctuation = /[,;:()[\]{}]/.test(text);
    
    return Math.min(1,
      (sentences.length > 1 ? 0.25 : 0) +
      (hasNumbers ? 0.25 : 0) +
      (hasSpecificTerms ? 0.25 : 0) +
      (hasPunctuation ? 0.25 : 0)
    );
  }

  private calculateInformationDensity(text: string, reply: TweetReply): number {
    const words = text.split(/\s+/);
    let informativeWords = 0;
    
    // Count technical terms, specific references, data points
    const informativePatterns = [
      /\b\d+(\.\d+)?%/,  // Percentages
      /\$[\d,]+/,        // Dollar amounts
      /\b(support|resistance|bullish|bearish)\b/i,
      /\b(analysis|chart|data|report)\b/i,
      /\b(because|since|due|given|considering)\b/i,
      /@\w+/,            // Mentions
      /\$[A-Z]{2,}/,     // Token symbols
    ];
    
    for (const word of words) {
      for (const pattern of informativePatterns) {
        if (pattern.test(word)) {
          informativeWords++;
          break;
        }
      }
    }
    
    // Factor in engagement relative to information
    const engagementRatio = (reply.likeCount + reply.retweetCount) / Math.max(text.length, 1);
    
    return Math.min(1, (informativeWords / Math.max(words.length, 1)) + (engagementRatio * 0.1));
  }

  private extractCredibilityMarkers(text: string): string[] {
    const markers: string[] = [];
    
    const credibilityPatterns = [
      { pattern: /https?:\/\/[^\s]+/g, marker: 'external_link' },
      { pattern: /\b(source|according\s+to|data\s+from)\b/i, marker: 'source_reference' },
      { pattern: /\b(research|study|analysis|report)\b/i, marker: 'research_reference' },
      { pattern: /\$[A-Z]{2,10}/g, marker: 'specific_token' },
      { pattern: /\d+(\.\d+)?%/g, marker: 'specific_percentage' },
      { pattern: /\$[\d,]+(\.\d+)?/g, marker: 'specific_amount' },
    ];
    
    for (const { pattern, marker } of credibilityPatterns) {
      if (pattern.test(text)) {
        markers.push(marker);
      }
    }
    
    return markers;
  }

  private calculateSpeculationLevel(text: string): number {
    let speculationScore = 0;
    
    const speculationPatterns = [
      /\b(might|maybe|possibly|could|probably|likely)\b/i,
      /\b(moon|pump|dump|rocket|gem)\b/i,
      /\b(next\s+\d+x|100x|1000x)\b/i,
      /\b(imo|imho|i\s+think|i\s+believe)\b/i,
    ];
    
    const certaintyPatterns = [
      /\b(will|definitely|certainly|guaranteed|sure)\b/i,
      /\b(fact|proven|confirmed|official)\b/i,
    ];
    
    for (const pattern of speculationPatterns) {
      if (pattern.test(text)) {
        speculationScore += 0.2;
      }
    }
    
    for (const pattern of certaintyPatterns) {
      if (pattern.test(text)) {
        speculationScore += 0.3; // Higher speculation for overconfident claims
      }
    }
    
    return Math.min(1, speculationScore);
  }

  private calculateEducationalValue(text: string): number {
    let educationalScore = 0;
    
    const educationalPatterns = [
      /\b(how\s+to|tutorial|guide|explanation|learn)\b/i,
      /\b(because|since|due\s+to|reason|explanation)\b/i,
      /\b(technical|fundamental|analysis)\b/i,
      /\b(definition|means|refers\s+to|stands\s+for)\b/i,
      /\b(example|instance|case\s+study)\b/i,
    ];
    
    for (const pattern of educationalPatterns) {
      if (pattern.test(text)) {
        educationalScore += 0.2;
      }
    }
    
    // Longer explanatory content tends to be more educational
    if (text.length > 100 && text.includes('because')) {
      educationalScore += 0.2;
    }
    
    return Math.min(1, educationalScore);
  }

  // Utility method to check if reply meets minimum quality standards
  isHighQuality(metrics: ContentQualityMetrics): boolean {
    const { botDetection } = config;
    
    return (
      metrics.genericityScore < botDetection.genericityThreshold &&
      metrics.spamProbability < botDetection.spamProbabilityThreshold &&
      metrics.originalityScore > 0.3 &&
      metrics.informationDensity > 0.2
    );
  }

  // Utility method to identify likely spam/bot content
  isLikelySpam(metrics: ContentQualityMetrics): boolean {
    const { botDetection } = config;
    
    return (
      metrics.genericityScore > botDetection.genericityThreshold ||
      metrics.spamProbability > botDetection.spamProbabilityThreshold ||
      metrics.manipulationScore > 0.5
    );
  }
}